import { useCallback } from 'react';
import type { MutableRefObject } from 'react';

type ValueSetter = (value: string) => void;

/**
 * Hook to enable setting the value of an input from its ref. The change
 * event will be dispatched on the input when the value is set.
 *
 * This factory hook uses `useCallback` internally, so that the function
 * returned is memoized and will only be recreated if the ref is replaced or
 * connected to a different element.
 *
 * Returns a function that takes a string to set the value of the input.
 *
 * @see useCallback
 */
export const useInputRefValueSetter = (
  inputRef: MutableRefObject<HTMLInputElement>,
): ValueSetter =>
  useCallback(
    (value) => {
      const descriptor = Object.getOwnPropertyDescriptor(
        inputRef.current,
        'value',
      );
      if (descriptor && descriptor.configurable) {
        delete inputRef.current.value;
      }
      inputRef.current.value = value;

      const e = document.createEvent('HTMLEvents');
      e.initEvent('change', true, false);
      inputRef.current.dispatchEvent(e);

      if (descriptor) {
        Object.defineProperty(inputRef.current, 'value', descriptor);
      }
    },
    [inputRef.current],
  );
